// Alpine sky + sun + the backdrop's aerial perspective + the far ring
// (ring.js) and the peak islands. Everything here renders with
// MeshBasicMaterial: lighting is baked, the atmosphere is a fragment term.
import * as THREE from 'three';
import { buildRingTex, gridSampler } from './ring.js';
import { ringTilesFault } from './ringtiers.js';
import { fetchJsonOnce } from './fetchonce.js';
import { fetchAsset } from './assetq.js';
import { act } from './sched.js';
import { runSteps } from './decode.js';
import { GU, bindGround, waterDecl, waterKey, setWaterFlow, setWaterSheets, WATER_CFG } from './groundmat.js';
import { WaterAreas, flowFieldSteps, riverSpeed } from './water.js';
import { classDraw, classOff } from './device.js';
import { ribbonUniforms, ribbonShader, ribbonOffsets } from './wire.js';

// ONE set of sky uniforms: the dome draws them, and the backdrop's haze
// takes its in-scatter colour from the same gradient in the view direction,
// so a far ridge fades INTO the sky behind it, never into a separate grey.
// THREE.Color(hex) is LINEAR; the dome ends in the renderer's tone curve +
// sRGB encode like every material, so a hex shows as authored (renderconfig
// look = neutral passes it unchanged below ~0.76 linear). mid is tuned to the
// owner's photo pair (clear-sky blue, owner-look-1).
const _sky = {
  top: { value: new THREE.Color(0x2d63b8) },
  mid: { value: new THREE.Color(0x80a9d6) },
  bot: { value: new THREE.Color(0xdfe9f3) },
  sunDir: { value: new THREE.Vector3(0.26, 0.79, 0.56).normalize() },
};
export const skyU = () => _sky;   // THE VR LOOK (vrstyle/fog.js) puts its void colour on the horizon

// the dome's sun = the region's BAKED sun (meta.lighting), so the disc, the
// haze glow and every baked shadow agree (game axes: +x east, +z south)
// also called by main.js boot with buildSky: the dome's sun must not wait
// for the far ring (buildPanorama, a deferred load) to be aimed
export function setSun(meta) {
  const lt = (meta && meta.lighting) || {};
  if (lt.sun_az_deg == null || lt.sun_alt_deg == null) return;
  const az = (lt.sun_az_deg * Math.PI) / 180, alt = (lt.sun_alt_deg * Math.PI) / 180;
  _sky.sunDir.value.set(Math.sin(az) * Math.cos(alt), Math.sin(alt),
    -Math.cos(az) * Math.cos(alt)).normalize();
}

export function buildSky(scene, tunables) {
  // 120 km dome (inside the 130 km camera far plane): the peak islands reach
  // 100 km. Pinned to the far plane (z = w) and drawn LAST among opaques with
  // LessEqual, it fills only the pixels nothing opaque covered — no full-screen
  // overdraw — and still precedes every transparent (horizon, clouds, water).
  // The epsilon keeps it strictly inside the far clip on every GPU; nothing
  // opaque lies beyond ~120 km, where it would tie the dome's depth.
  const skyGeo = new THREE.SphereGeometry(120000, 24, 12);
  const skyMat = new THREE.ShaderMaterial({
    side: THREE.BackSide, depthWrite: false, depthTest: true,
    depthFunc: THREE.LessEqualDepth, fog: false,
    uniforms: _sky,
    vertexShader: `
      varying vec3 vDir;
      void main() {
        vDir = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        gl_Position.z = gl_Position.w * (1.0 - 1e-7);
      }`,
    fragmentShader: `
      varying vec3 vDir;
      uniform vec3 top, mid, bot, sunDir;
      void main() {
        float h = clamp(vDir.y, 0.0, 1.0);
        vec3 c = mix(bot, mid, smoothstep(0.0, 0.18, h));
        c = mix(c, top, smoothstep(0.18, 0.65, h));
        float s = max(dot(vDir, sunDir), 0.0);
        c += vec3(1.0, 0.95, 0.8) * pow(s, 600.0) * 1.2;   // sun disc
        c += vec3(1.0, 0.9, 0.7) * pow(s, 8.0) * 0.06;     // halo
        gl_FragColor = vec4(c, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  });
  const sky = new THREE.Mesh(skyGeo, skyMat);
  sky.name = 'sky';
  sky.renderOrder = 100;
  sky.frustumCulled = false;
  sky.matrixAutoUpdate = false;   // static (as the horizon, sheets, rivers): pinned by the shader, never moved
  scene.add(sky);
  scene.fog = new THREE.FogExp2(0xc9d9ec, (tunables.world && tunables.world.fog_density) || 0.000045);
  return sky;
}

// ------------------------------------------------- backdrop atmosphere
// Aerial perspective for the ring and the peak islands (APP_HORIZON.md §6).
// The ray from the eye to a backdrop fragment is split where it leaves the
// playable frame:
//   inside  — the scene's FogExp2, exactly the terrain's formula on the
//             inside part of the depth, so ring and terrain agree at the edge;
//   outside — extinction + in-scatter through two exponential layers:
//             Rayleigh (per channel, beta = 5.8/13.5/33.1e-6 per m at sea
//             level, scale height 8 km: far ridges go BLUE, snow keeps its
//             white) and the boundary-layer deck (sigma_deck at deck_m,
//             scale_height_m: the valleys outside drown, summits stay crisp).
// In-scatter colour = the dome's gradient in the view direction plus a
// forward-scatter glow toward the sun. Nothing blurs: only colour moves.
// uHazeMul is shared by every backdrop material — the monocular render pass
// lowers it (optical dehaze).

const _hazeMul = { value: 1 };
export function setHazeMul(v) { _hazeMul.value = v; }

function atmosParams(tunables, meta) {
  const h = (tunables && tunables.world && tunables.world.haze) || {};
  const bands = (meta && meta.bands) || {};
  const deck = (meta && meta.horizon && meta.horizon.deck_m)
    || ((Number.isFinite(bands.treeline_m) ? bands.treeline_m : 2200) + 400);
  const bb = (meta && meta.bbox) || { e0: 0, e1: 0, n0: 0, n1: 0 };
  const rm = h.rayleigh_mul != null ? h.rayleigh_mul : 1;
  return {
    sigma: h.sigma_deck != null ? h.sigma_deck : 1.5e-5,
    scaleH: h.scale_height_m || 350,
    deck,
    betaR: new THREE.Vector3(5.8e-6 * rm, 13.5e-6 * rm, 33.1e-6 * rm),
    glow: h.sun_glow != null ? h.sun_glow : 0.35,
    frame: new THREE.Vector4(0, 0, bb.e1 - bb.e0, bb.n1 - bb.n0),
  };
}

// Returns atmos(mat, skirt, extra, key): injects the split atmosphere into a
// MeshBasicMaterial (+ the islands' below-deck alpha skirt); `extra` may
// patch the shader further (ring.js derives its uv there).
function makeAtmos(p) {
  return (mat, skirt, extra, key) => {
    mat.fog = true;
    const u = {
      uHazeSigma: { value: p.sigma },
      uHazeH: { value: p.scaleH },
      uHazeDeck: { value: p.deck },
      uHazeMul: _hazeMul,
      uBetaR: { value: p.betaR },
      uGlow: { value: p.glow },
      uFrame: { value: p.frame },
      uSkirtLo: { value: skirt ? skirt.lo : -1e7 },
      uSkirtHi: { value: skirt ? skirt.hi : -1e7 + 1 },
      skyBot: _sky.bot, skyMid: _sky.mid, skySun: _sky.sunDir,
    };
    mat.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, u);
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vHazeW;')
        .replace('#include <begin_vertex>',
          '#include <begin_vertex>\nvHazeW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', `#include <common>
varying vec3 vHazeW;
uniform float uHazeSigma, uHazeH, uHazeDeck, uHazeMul, uGlow, uSkirtLo, uSkirtHi;
uniform vec3 uBetaR, skyBot, skyMid, skySun;
uniform vec4 uFrame;
float gTin = 1.0;   // fraction of the eye ray inside the playable frame`)
        .replace('#include <opaque_fragment>', `{
  vec3 V = vHazeW - cameraPosition;
  float D = length(V);
  vec3 dir = V / max(D, 1e-3);
  // where the ray leaves the frame (xz slab); an eye outside it has no inside path
  vec2 c = cameraPosition.xz, d = V.xz;
  bool inF = c.x >= uFrame.x && c.x <= uFrame.z && c.y >= uFrame.y && c.y <= uFrame.w;
  vec2 lim = vec2(d.x >= 0.0 ? uFrame.z : uFrame.x, d.y >= 0.0 ? uFrame.w : uFrame.y);
  vec2 tt = vec2(abs(d.x) > 1e-3 ? (lim.x - c.x) / d.x : 1e9, abs(d.y) > 1e-3 ? (lim.y - c.y) / d.y : 1e9);
  gTin = inF ? clamp(min(tt.x, tt.y), 0.0, 1.0) : 0.0;
  float Dout = D * (1.0 - gTin);
  float y0 = cameraPosition.y + V.y * gTin;
  float dy = vHazeW.y - y0;
  // closed-form mean density of two exponential layers along the segment
  float a0 = exp(-clamp((y0 - uHazeDeck) / uHazeH, -4.0, 60.0));
  float a1 = exp(-clamp((vHazeW.y - uHazeDeck) / uHazeH, -4.0, 60.0));
  float mie = abs(dy) > 1.0 ? clamp((a0 - a1) * uHazeH / dy, 0.0, 54.6) : 0.5 * (a0 + a1);
  float r0 = exp(-max(y0, 0.0) / 8000.0), r1 = exp(-max(vHazeW.y, 0.0) / 8000.0);
  float ray = abs(dy) > 1.0 ? (r0 - r1) * 8000.0 / dy : 0.5 * (r0 + r1);
  vec3 T = exp(-(uBetaR * ray + vec3(uHazeSigma * mie)) * Dout * uHazeMul);
  // the dome's colour in this direction: linear, like the dome's own output
  // before the shared tone curve (both pass it once, so they agree)
  vec3 sky = mix(skyBot, skyMid, smoothstep(0.0, 0.18, clamp(dir.y, 0.0, 1.0)));
  float cs = max(dot(dir, skySun), 0.0);
  sky += vec3(1.0, 0.93, 0.8) * (uGlow * pow(cs, 4.0) + 0.06 * pow(cs, 8.0));
  outgoingLight = outgoingLight * T + sky * (1.0 - T);
  diffuseColor.a *= smoothstep(uSkirtLo, uSkirtHi, vHazeW.y);
}
#include <opaque_fragment>`)
        .replace('#include <fog_fragment>', `#ifdef USE_FOG
  {
    // the scene fog over the INSIDE part of the ray only (terrain's formula)
    float fd = vFogDepth * gTin;
    float fogFactor = 1.0 - exp(-fogDensity * fogDensity * fd * fd);
    gl_FragColor.rgb = mix(gl_FragColor.rgb, fogColor, fogFactor);
  }
#endif`);
      if (extra) extra(sh);
    };
    mat.customProgramCacheKey = () => 'atmos-' + (key || '') + (skirt ? '-skirt' : '');
    return mat;
  };
}

// the backdrop atmosphere injector for this world (tools/shaderprobe.mjs
// compiles ring.js's sector materials with it; terrain.js the far tree's)
export function atmosFor(tunables, meta) { return makeAtmos(atmosParams(tunables, meta)); }

// the far ring's textures (ring.js buildRingTex, APP_HORIZON.md §8c; H1: the
// far MESH is the ground tree's outer nodes, terrain.js): a bake whose
// meta.panorama is not the far field's data contract (the core, the sampler,
// the world line) + panorama.classes of ringtiers.js RING_CLASSES_FORMAT
// throws here (ringTilesFault names what is missing), like the terrain's
// missing meta.tex.light.white; so does a core the queue could not fetch
// (ring.js buildRingTex). Returns rt = { look, sectors, border, height, atlas,
// version, onChange, want, ready, relief, commit, setGenerated, fallback }
// (the world line joins the scene here). Per sector, the coarsest generated
// level at boot; `rt.want({ eye, pa })` refines it (main.js ringTick, then the
// far draw). opts: { atlas: terrain.farAtlas (null: nothing generated),
// getRenderer: () => the WebGLRenderer the atlas lives on (ringgen.js),
// gmIndex: the ground tree's index promise (ring.js: the relief rows) }.
export async function buildPanorama(scene, meta, baseUrl, tunables, opts = {}) {
  setSun(meta);
  const fault = ringTilesFault(meta.panorama);
  if (fault) throw new Error(`bake has no far ring (${fault})`);
  const rt = await buildRingTex(meta, baseUrl, makeAtmos(atmosParams(tunables, meta)), { atlas: opts.atlas || null, getRenderer: opts.getRenderer, gmIndex: opts.gmIndex || null });
  if (rt.border) scene.add(rt.border);
  return rt;
}

// ------------------------------------------------------- peak islands
// horizon.bin (bake rev 81, APP_HORIZON.md): coarse curved heights over the
// full horizon square (the gazetteer's far occlusion instrument) + a draw
// mask of viewshed-visible above-deck cells + per-cell Sentinel-2 color
// (glaciers read as glaciers). Only masked cells become geometry — distant
// massifs float above the haze deck, their below-deck skirt alpha-faded by
// the haze shader. Everything below stays absent on purpose.
// R8d6c: the build is a STEP GENERATOR (decode.js runSteps; the live profile
// held it in ONE 60-73 ms task after the Beam: buildHorizon 16-19 ms, vert
// 10-16, h 10, three's SRGBToLinear 11): a step per HORIZON_STEP drawn
// quads of the cell scan (each quad's up to 4 new vertices: the heights,
// the normal, the colour + its SRGBToLinear, the light), then the
// geometry's assembly a step per attribute / the index / the bounding
// sphere, the material + mesh the last; the same code in the same order —
// only yields between the units, so the same bytes (check-mapfirst l). A
// quad-scan yield gives the quads drawn so far (the gate's step bound;
// runSteps ignores it). buildHorizon fetches, runs it under 'horizon.build'
// and adds the mesh; the same { mesh, height } (or null). `join(mesh)`: a
// promise the add waits for, the sampler returned at once (main.js: its program through the compile queue
// first — 'atmos-islands-skirt' was built and linked inside a draw, 281 ms).
export const HORIZON_STEP = 2048;

export async function buildHorizon(scene, meta, baseUrl, tunables, join = null) {
  const hz = meta.horizon;
  if (!hz) return null;
  // phase 1 (location-independent): the asset queue's bulk class
  const ab = await fetchAsset(`${baseUrl}/horizon.bin`, { prio: 'bulk' });
  const r = await runSteps(horizonSteps(ab, meta, tunables), 'horizon.build');
  if (!r || !r.mesh) return r;
  // the sampler at once; the mesh after join (a failed join adds it too)
  if (join) { Promise.resolve().then(() => join(r.mesh)).catch(() => {}).then(() => scene.add(r.mesh)); return r; }
  act('horizon.build');
  scene.add(r.mesh);
  return r;
}

export function* horizonSteps(ab, meta, tunables) {
  if (!ab || ab.byteLength < 36) return null;
  const dv = new DataView(ab);
  if (dv.getUint32(0, false) !== 0x48525a31) return null;   // 'HRZ1'
  const N = dv.getUint32(4, true);
  const sp = dv.getFloat32(8, true);
  const ox = dv.getFloat32(12, true);
  const oz = dv.getFloat32(16, true);
  const zBase = dv.getFloat32(20, true);
  const zScale = dv.getFloat32(24, true) / 100;
  const deck = dv.getFloat32(28, true);
  const fade = dv.getFloat32(32, true);
  if (ab.byteLength < 36 + N * N * 6) return null;
  const data = new Uint16Array(ab, 36, N * N);
  const mask = new Uint8Array(ab, 36 + N * N * 2, N * N);
  const rgb = new Uint8Array(ab, 36 + N * N * 3, N * N * 3);
  const h = (i, j) => data[j * N + i] * zScale + zBase;

  // the world's ONE light model, as the bake ships it: the sun from
  // meta.lighting, the hemisphere/sun colours and the white point W from
  // meta.tex.light (bake light_irradiance) — island vertex light is E / W
  // like every baked texel, the material gain W undoes it (no shadows: the
  // islands have no occluder model). Game axes: +x east, +z south, y up.
  const lt = meta.lighting;
  if (!lt || lt.sun_az_deg == null || lt.sun_alt_deg == null) {
    throw new Error('bake has no meta.lighting sun');
  }
  const lm = meta.tex && meta.tex.light;
  if (!(lm && lm.white > 0)) {
    throw new Error('bake has no meta.tex.light.white (texture rev >= 3 required)');
  }
  const az = (lt.sun_az_deg * Math.PI) / 180;
  const alt = (lt.sun_alt_deg * Math.PI) / 180;
  const sun = new THREE.Vector3(Math.sin(az) * Math.cos(alt), Math.sin(alt),
    -Math.cos(az) * Math.cos(alt)).normalize();
  const E = new Float32Array(3);
  const irradiance = (lam, ny) => {
    const w = 0.5 * ny + 0.5;
    for (let ch = 0; ch < 3; ch++) {
      const hemi = lm.ground[ch] + w * (lm.sky[ch] - lm.ground[ch]);
      E[ch] = Math.min(1, (hemi + lam * lm.sun[ch]) / lm.white);
    }
    return E;
  };

  const cSnow = new THREE.Color(0xf2f5f8), cRock = new THREE.Color(0x8a8078);
  const pos = [], col = [], idx = [];
  const vid = new Int32Array(N * N).fill(-1);
  const _c = new THREE.Color();
  const vert = (i, j) => {
    let v = vid[j * N + i];
    if (v >= 0) return v;
    const y = h(i, j);
    const i0 = Math.max(0, i - 1), i1 = Math.min(N - 1, i + 1);
    const j0 = Math.max(0, j - 1), j1 = Math.min(N - 1, j + 1);
    const nx = -(h(i1, j) - h(i0, j)) / ((i1 - i0) * sp);
    const nz = -(h(i, j1) - h(i, j0)) / ((j1 - j0) * sp);
    const inv = 1 / Math.hypot(nx, 1, nz);
    const k = j * N + i;
    if (rgb[k * 3] || rgb[k * 3 + 1] || rgb[k * 3 + 2]) {
      _c.setRGB(rgb[k * 3] / 255, rgb[k * 3 + 1] / 255, rgb[k * 3 + 2] / 255,
        THREE.SRGBColorSpace);
      // lift Sentinel-2's flat noon look a touch toward snow at altitude
      if (y > deck + 500) _c.lerp(cSnow, 0.15);
    } else {
      // no imagery fetched: elevation ramp fallback (rock below, snow above)
      _c.copy(cRock).lerp(cSnow, Math.min(1, Math.max(0, (y - deck - 200) / 500)));
    }
    const e = irradiance(Math.max(0, nx * inv * sun.x + inv * sun.y + nz * inv * sun.z), inv);
    _c.setRGB(_c.r * e[0], _c.g * e[1], _c.b * e[2]);
    v = pos.length / 3;
    vid[j * N + i] = v;
    pos.push(ox + i * sp, y, oz + j * sp);
    col.push(_c.r, _c.g, _c.b);
    return v;
  };
  for (let j = 0; j < N - 1; j++) {
    for (let i = 0; i < N - 1; i++) {
      if (!(mask[j * N + i] && mask[j * N + i + 1]
        && mask[(j + 1) * N + i] && mask[(j + 1) * N + i + 1])) continue;
      const a = vert(i, j), b = vert(i + 1, j);
      const c2 = vert(i, j + 1), d = vert(i + 1, j + 1);
      idx.push(a, c2, b, b, c2, d);
      if (idx.length % (6 * HORIZON_STEP) === 0) yield idx.length / 6;
    }
  }
  yield idx.length / 6;
  const height = gridSampler(data, N, sp, ox, oz, zScale, zBase);
  if (!idx.length) return { mesh: null, height };
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  yield;
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  yield;
  geo.setIndex(idx);
  yield;
  geo.computeBoundingSphere();
  yield;
  const mat = new THREE.MeshBasicMaterial({
    vertexColors: true, transparent: true, depthWrite: true,
  });
  mat.color.setScalar(lm.white);        // linear gain, like the terrain's
  setSun(meta);
  makeAtmos(atmosParams(tunables, meta))(mat, { lo: deck - fade, hi: deck }, null, 'islands');
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'horizon';
  mesh.renderOrder = -1;      // among transparents: before the clouds
  mesh.matrixAutoUpdate = false;
  return { mesh, height };
}


// ---------------------------------------------------------------- clouds
// Painterly cumulus billboards: Y-axis billboarded planes (flat bottoms stay
// level) drifting slowly east. Deterministic placement; ~12 transparent
// quads — negligible cost, big "animation film" payoff against the flat sky.

const _clouds = [];

function _ch(i, s) {
  let h = (Math.imul(i + 1, 374761393) + Math.imul(s, 668265263)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function makeCloudTexture(seed) {
  const W = 256, H = 160;
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const g = cv.getContext('2d');
  g.clearRect(0, 0, W, H);
  const base = H * 0.76;                 // flat-ish underside
  // shaded underside blobs first, then bright tops — painterly stacking.
  // by is clamped so gradients never leave the canvas (a blob past the top
  // edge rendered as a hard horizontal crop line in the sky).
  for (const [tint, count, lift, rMin] of [
    ['203,213,228', 9, 0.16, 16], ['232,238,246', 9, 0.34, 14], ['255,255,255', 7, 0.52, 10]]) {
    for (let b = 0; b < count; b++) {
      const u = (b + 0.5) / count;
      const bx = W * (0.12 + u * 0.76) + (_ch(b, seed) - 0.5) * 22;
      const spread = 1 - Math.abs(u - 0.5) * 1.6;   // dome profile
      const r = rMin + _ch(b, seed + 7) * 18 * (0.5 + spread);
      const by = Math.max(r + 3,
        base - lift * H * 0.8 * (0.4 + spread) - _ch(b, seed + 3) * 10);
      const gr = g.createRadialGradient(bx, by, r * 0.15, bx, by, r);
      gr.addColorStop(0, `rgba(${tint},0.95)`);
      gr.addColorStop(1, `rgba(${tint},0)`);
      g.fillStyle = gr;
      g.beginPath(); g.arc(bx, by, r, 0, Math.PI * 2); g.fill();
    }
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  return tex;
}

export function buildClouds(scene, meta) {
  const bb1 = meta.bbox;
  const cx = (bb1.e1 - bb1.e0) / 2;
  const cz = (bb1.n1 - bb1.n0) / 2;
  // ONE pass (vr-smooth): three draws a transparent DoubleSide material in two
  // passes (back faces, then front) unless forceSinglePass — a billboard plane
  // shows one face per pixel, so the second pass only cost a draw (Quest:
  // 12 clouds = 24 draws an eye, the frame is draw-submission bound)
  const mats = [0, 1, 2].map((s) => new THREE.MeshBasicMaterial({
    map: makeCloudTexture(s * 11 + 5), transparent: true, opacity: 0.92,
    depthWrite: false, fog: true, side: THREE.DoubleSide, forceSinglePass: true,
  }));
  // sunny-day cumulus float ABOVE the skyline (APP_HORIZON §8b): the flat
  // bottom sits >= 300 m over the highest backdrop summit in meta (the far
  // ring's and the peak islands' elevation stats), so from a summit the
  // clouds clear the far peaks instead of lying on them. Never lower than
  // 3300 m.
  const pe = meta.panorama && meta.panorama.elevation, he = meta.horizon && meta.horizon.elevation;
  if (!(pe && Number.isFinite(pe.elev_max_m))) throw new Error('bake has no meta.panorama.elevation.elev_max_m');
  if (!(he && Number.isFinite(he.max_m))) throw new Error('bake has no meta.horizon.elevation.max_m');
  const skyline = Math.max(pe.elev_max_m, he.max_m) + 300;
  // quest-merge (2026-11-01): with CLASS_OFF cloud_sort the 12 clouds are ONE
  // InstancedMesh per material (3 draws, never culled: 2 tris an instance,
  // the ring spans the world) at renderOrder -1 — the first transparents, as
  // the farthest; the 4 instances of a material blend in slot order, not
  // depth-sorted (the owner-approved subtle trade). Elsewhere a Mesh each,
  // depth-sorted by three.
  const inst = classOff('cloud_sort') ? mats.map((mat) => {
    const im = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), mat, 4);
    im.count = 0;
    im.frustumCulled = false;
    im.renderOrder = -1;
    im.name = 'clouds';
    scene.add(im);
    return im;
  }) : null;
  for (let i = 0; i < 12; i++) {
    const ang = _ch(i, 1) * Math.PI * 2;
    const rad = 3500 + _ch(i, 2) * 8500;
    const w = 900 + _ch(i, 3) * 1300;
    const m = inst ? new THREE.Object3D() : new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mats[i % 3]);
    m.scale.set(w, w * 0.52, 1);   // matches the 256x160 texture aspect
    const base = Math.max(3300, skyline + 0.26 * w);   // 0.26 w = half the plane height
    m.position.set(cx + Math.cos(ang) * rad, base + _ch(i, 4) * 900, cz + Math.sin(ang) * rad);
    m.userData.drift = 1.5 + _ch(i, 5) * 2.5;
    m.userData.cx = cx;
    m.name = 'cloud';
    if (inst) { m.userData.inst = inst[i % 3]; m.userData.slot = inst[i % 3].count++; } else scene.add(m);
    _clouds.push(m);
  }
}

export function updateClouds(camPos, dt) {
  for (const m of _clouds) {
    m.position.x += m.userData.drift * dt;
    if (m.position.x > m.userData.cx + 14000) m.position.x -= 28000;
    m.rotation.y = Math.atan2(camPos.x - m.position.x, camPos.z - m.position.z);
    const im = m.userData.inst;            // the instanced path: this cloud's slot carries its matrix
    if (im) { m.updateMatrix(); im.setMatrixAt(m.userData.slot, m.matrix); im.instanceMatrix.needsUpdate = true; }
  }
}

// The water clock — main.js ticks waterTime.value += dt. It IS groundmat's
// uWT: the terrain's water surface and the stream ribbons run on one clock.
export const waterTime = GU.uWT;

// ------------------------------------------------------------- water meshes
// WATER-G (APP_SURFACE.md "Water (WATER-G)"). Water on class-254 cells
// (lakes, ponds, river areas) is the TERRAIN's (groundmat waterDecl). Two
// kinds of water keep a mesh, both drawn with the very same functions:
//   * stream ribbons — the water the class map paints as gravel riverbed
//     (streams, TLM runs, rivers without a river area);
//   * sheets — a `sheet` lake (water.json, bake.py SHEET_MIN_M: a drawn-down
//     reservoir whose LiDAR caught the bed) at level_m over its gravel bed.
// The colour: the painted dark teal (renderconfig ground.water.tint_srgb =
// the painter's PALETTE water deep) x the bake's light, the shared ripple
// field, fresnel sky + sun glint — all in LINEAR before the tone curve
// (after opaque_fragment). Transparent: a transmittance T x (1 - F) lets
// the bed below show; src = colour / alpha so the blend adds exactly
// colour + T (1 - F) x what is behind.
const _srgbLin = (c) => { const v = c / 255; return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
const _f4 = (x) => Number(x).toFixed(4);
// vDecl / vMain: the extra vertex declarations / body (vWp is given);
// fMain: GLSL that sets `float T` (transmittance), `vec2 v` (flow m/s) and
// may adjust `col` / `a` / `k` (coverage) after the water (`// @after`)
function _waterMaterial(key, vDecl, vMain, fPre, fPost) {
  const W = WATER_CFG;
  const lin = W.tint_srgb.map(_srgbLin);
  const mat = new THREE.MeshBasicMaterial({
    color: 0xffffff, transparent: true, fog: true,
    depthWrite: false, side: THREE.DoubleSide,
    // ONE pass (TRI-CUTS): three draws a transparent DoubleSide material
    // twice (back faces, then front), but a sheet or a ribbon is one layer
    // that never overlaps itself, so the back pass drew no pixel the front
    // pass would not; DoubleSide stays (seen from below while swimming)
    forceSinglePass: true,
  });
  mat.onBeforeCompile = (sh) => {
    bindGround(sh.uniforms);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>
        varying vec3 vWp;${vDecl}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        vWp = (modelMatrix * vec4(position, 1.0)).xyz;${vMain}`);
    const decl = waterDecl() || `
        uniform vec3 uSunL; uniform vec3 uSky; uniform vec3 uSunC;
        uniform float uWT; uniform highp sampler2D uWater; uniform vec4 uWaterInfo;`;
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
        varying vec3 vWp;${vDecl.replace(/attribute [^;]*;/g, '')}` + decl)
      .replace('#include <opaque_fragment>', `#include <opaque_fragment>
        {
          float fw = max(length(dFdx(vWp.xz)), length(dFdy(vWp.xz)));
          vec3 E = uSky + uSunC * max(uSunL.y, 0.0);
          float T = 1.0, k = 1.0;
          vec2 v = vec2(0.0);${fPre}
          vec3 tintE = vec3(${lin.map(_f4).join(', ')}) * E;
          float F = 0.0;
          vec3 col = tintE * (1.0 - T);
          float a = 1.0 - T;` + (waterDecl() ? `
          vec3 surf = wtrSurf(wtrSlope(vWp.xz, v, fw), vWp, 1.0, F);
          col = tintE * ((1.0 - T) * (1.0 - F)) + surf;
          a = 1.0 - T * (1.0 - F);` : '') + `${fPost}
          gl_FragColor = vec4(col / max(a, 1e-4), a * k);
        }`);
  };
  mat.customProgramCacheKey = () => `${key}-${waterKey()}`;
  return mat;
}

// Streams: transmittance stream_clear, the flow along the ribbon (aDir x
// the speed); the flow map (two phase-offset flowPat layers along the arc
// length s) drives the white water: baked foam (check-dam sheets, plunge
// bands, steep reaches) + fast-water banks. Edges soften over the last 30 %
// of the half width (aFlow.w = -1..1); a ribbon fades out where the class
// map paints water (uWater coverage), so no run doubles the terrain's.
export function streamMaterial() {
  const W = WATER_CFG;
  const T = Math.min(1, Math.max(0, Number(W.stream_clear)));
  const [E0, E1] = W.edge.map(Number);
  return thinStream(_waterMaterial('stream', `
        attribute vec4 aFlow; attribute vec2 aDir; varying vec4 vFlow; varying vec2 vDir;`,
  `
        vFlow = aFlow; vDir = aDir;`, `
          T = ${_f4(T)};
          v = vDir * vFlow.y;`, `
          float s = vFlow.x, sp = vFlow.y, foam = vFlow.z, l = vFlow.w;
          // the flow map: two phase-offset layers along s cross-fade
          const float TP = 1.4;
          float p0 = fract(uWT / TP), p1 = fract(uWT / TP + 0.5);
          float pat = mix(0.5 * sin((s - sp * p0 * TP) * 1.9 + sin(l * 2.6 + (s - sp * p0 * TP) * 0.37) * 1.5)
                        + 0.5 * sin((s - sp * p0 * TP) * 4.3 - l * 1.3 + sin((s - sp * p0 * TP) * 0.9 + l) * 2.0),
                          0.5 * sin((s + 5.3 - sp * p1 * TP) * 1.9 + sin(l * 2.6 + (s + 5.3 - sp * p1 * TP) * 0.37) * 1.5)
                        + 0.5 * sin((s + 5.3 - sp * p1 * TP) * 4.3 - l * 1.3 + sin((s + 5.3 - sp * p1 * TP) * 0.9 + l) * 2.0),
                          abs(1.0 - 2.0 * p0));
          // white water: the sheet + plunge band (baked foam), fast-water banks
          float bank = smoothstep(0.7, 1.0, abs(l)) * clamp(sp / 3.0, 0.0, 1.0) * 0.45;
          float wf = clamp(smoothstep(0.15, 0.85, (foam + bank) * (0.7 + 0.5 * pat)), 0.0, 1.0);
          col = mix(col, E * (0.8 * 0.96), wf);   // (col is premultiplied)
          a = mix(a, 0.96, wf);
          // soft edges; no ribbon where the class map paints water
          float cov = textureLod(uWater, vWp.xz * uWaterInfo.xy, 0.0).a;
          k = (1.0 - smoothstep(0.7, 1.0, abs(l))) * (1.0 - smoothstep(${_f4(E0)}, ${_f4(E1)}, cov));`));
}
// vr-shimmer: a stream ribbon is a FLAT thin ribbon (wire.js): seen at a grazing angle a 2-10 m run is
// thinner than a pixel and went on / off ("rivers … go off and on"); each node's vertex pair (aOff,
// buildRivers) is widened to RIBBON_MIN_PX on screen (in screen space: `transformed` stays the true
// vertex, so vWp — the ripple, the uWater fade and the light read — is the ribbon's own point), the
// coverage (vRbCov) its alpha
function thinStream(mat) {
  const water = mat.onBeforeCompile, key = mat.customProgramCacheKey;
  ribbonUniforms(mat);
  mat.onBeforeCompile = (sh) => {
    water(sh);
    ribbonShader(sh, mat, 'flat');
    sh.vertexShader = sh.vertexShader.replace('vWp = (modelMatrix * vec4(position, 1.0)).xyz;', 'vWp = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    sh.fragmentShader = sh.fragmentShader.replace('gl_FragColor = vec4(col / max(a, 1e-4), a * k);', 'gl_FragColor = vec4(col / max(a, 1e-4), a * k * vRbCov);');
  };
  mat.customProgramCacheKey = () => key() + '-thin2';
  return mat;
}

// Sheets: the wind drift alone; the transmittance runs from 1 at the shore
// (aShore = the vertex's distance to it, m) to sheet_clear sheet_ramp_m in.
export function sheetMaterial() {
  const W = WATER_CFG;
  const R = Math.max(0.1, Number(W.sheet_ramp_m));
  const C = Math.min(1, Math.max(0, Number(W.sheet_clear)));
  return _waterMaterial('sheet', `
        attribute float aShore; varying float vShore;`, `
        vShore = aShore;`, `
          T = mix(1.0, ${_f4(C)}, smoothstep(0.0, ${_f4(R)}, vShore));`, '');
}

// A sheet lake's geometry: its polygon (+ holes) triangulated at level_m,
// refined by longest-edge bisection (Rivara) wherever a triangle reaches
// into the ramp and its longest edge is > SHEET_EDGE_M; a split edge splits
// in its neighbour too (conforming: no T-junction, no alpha seam). aShore =
// each vertex's distance to the nearest ring (shore or island), capped.
// R8d: a STEP GENERATOR (decode.js runSteps; one big lake was a 2 s task,
// water.sheets): a step per lake, per SHEET_STEP triangles of the
// refinement / the re-check pass, per SHEET_STEP vertices; the same loops in
// the same order — sheetGeometry = its drain, the same bytes. R8d2: the
// index copy per 3 x SHEET_STEP entries, and the geometry's assembly a step
// per attribute / the index / the bounding sphere (the live bake's last step
// held them all: 12 ms on a desktop).
const SHEET_EDGE_M = 2.0;
const SHEET_MAX_TRIS = 200000;
const SHEET_STEP = 1024;
const drain = (it) => { for (;;) { const r = it.next(); if (r.done) return r.value; } };
export function sheetGeometry(lakes, rampM) { return drain(sheetGeometrySteps(lakes, rampM)); }
export function* sheetGeometrySteps(lakes, rampM) {
  const pos = [], shore = [], idx = [];
  const cap = rampM + SHEET_EDGE_M;
  for (const lake of lakes) {
    if (!lake.polygon || lake.polygon.length < 3) continue;
    const rings = [lake.polygon, ...(lake.holes || []).filter((h) => h && h.length >= 3)];
    const v2 = (r) => r.map(([x, z]) => new THREE.Vector2(x, z));
    const contour = v2(rings[0]);
    const holes = rings.slice(1).map(v2);
    const faces = THREE.ShapeUtils.triangulateShape(contour, holes);   // drops a closing duplicate in place
    const P = [...contour, ...holes.flat()];
    // shore segments, bucketed (CELL m)
    const CELL = 8, segs = new Map();
    for (const r of rings) {
      for (let i = 0; i < r.length; i++) {
        const a = r[i], b = r[(i + 1) % r.length];
        const s = [a[0], a[1], b[0], b[1]];
        const x0 = Math.floor((Math.min(a[0], b[0]) - cap) / CELL), x1 = Math.floor((Math.max(a[0], b[0]) + cap) / CELL);
        const z0 = Math.floor((Math.min(a[1], b[1]) - cap) / CELL), z1 = Math.floor((Math.max(a[1], b[1]) + cap) / CELL);
        for (let cz = z0; cz <= z1; cz++) {
          for (let cx = x0; cx <= x1; cx++) {
            const k = cx + '_' + cz;
            const c = segs.get(k);
            if (c) c.push(s); else segs.set(k, [s]);
          }
        }
      }
    }
    const dist = (x, z) => {
      const c = segs.get(Math.floor(x / CELL) + '_' + Math.floor(z / CELL));
      let d = cap;
      if (c) {
        for (const s of c) {
          const dx = s[2] - s[0], dz = s[3] - s[1];
          const t = Math.max(0, Math.min(1, ((x - s[0]) * dx + (z - s[1]) * dz) / (dx * dx + dz * dz + 1e-12)));
          const e = Math.hypot(x - (s[0] + dx * t), z - (s[1] + dz * t));
          if (e < d) d = e;
        }
      }
      return d;
    };
    yield;                                   // triangulated + the shore buckets
    const X = [], Z = [], D = [];
    const vert = (x, z) => { X.push(x); Z.push(z); D.push(dist(x, z)); return X.length - 1; };
    for (let i = 0; i < P.length; i++) { vert(P[i].x, P[i].y); if (i % SHEET_STEP === SHEET_STEP - 1) yield; }
    const mids = new Map();
    const key = (i, j) => (i < j ? i + '_' + j : j + '_' + i);
    const len2 = (i, j) => (X[i] - X[j]) ** 2 + (Z[i] - Z[j]) ** 2;
    const mid = (i, j) => {
      const k = key(i, j);
      let m = mids.get(k);
      if (m === undefined) { m = vert((X[i] + X[j]) / 2, (Z[i] + Z[j]) / 2); mids.set(k, m); }
      return m;
    };
    // split triangle t at its edge e (0: ab, 1: bc, 2: ca)
    const cut = (t, e, into) => {
      const [a, b, c] = t;
      if (e === 0) { const m = mid(a, b); into.push([a, m, c], [m, b, c]); }
      else if (e === 1) { const m = mid(b, c); into.push([a, b, m], [a, m, c]); }
      else { const m = mid(c, a); into.push([a, b, m], [m, b, c]); }
    };
    const longest = (t) => {
      const l = [len2(t[0], t[1]), len2(t[1], t[2]), len2(t[2], t[0])];
      return l[0] >= l[1] && l[0] >= l[2] ? 0 : (l[1] >= l[2] ? 1 : 2);
    };
    const want = (t) => {
      const e = longest(t);
      if (len2(t[e], t[(e + 1) % 3]) <= SHEET_EDGE_M * SHEET_EDGE_M) return -1;
      if (Math.min(D[t[0]], D[t[1]], D[t[2]]) < rampM) return e;
      return dist((X[t[0]] + X[t[1]] + X[t[2]]) / 3, (Z[t[0]] + Z[t[1]] + Z[t[2]]) / 3) < rampM ? e : -1;
    };
    // an edge already split elsewhere: this triangle must split it too
    // (longest first keeps the bisection Rivara's)
    const pending = (t) => {
      const e = longest(t);
      if (mids.has(key(t[e], t[(e + 1) % 3]))) return e;
      for (let k = 0; k < 3; k++) if (mids.has(key(t[k], t[(k + 1) % 3]))) return e;
      return -1;
    };
    let out = [];
    let stack = faces.map((f) => f.slice());
    let n = 0;
    for (let pass = 0; pass < 64 && stack.length; pass++) {
      while (stack.length) {
        const t = stack.pop();
        const budget = out.length + stack.length < SHEET_MAX_TRIS;
        let e = pending(t);
        if (e < 0 && budget) e = want(t);
        if (e < 0) out.push(t); else cut(t, e, stack);
        if (++n % SHEET_STEP === 0) yield;
      }
      // a triangle emitted before its neighbour split a shared edge
      const keep = [];
      for (let i = 0; i < out.length; i++) {
        const t = out[i];
        (pending(t) >= 0 ? stack : keep).push(t);
        if (++n % SHEET_STEP === 0) yield;
      }
      out = keep;
    }
    out = out.flat();
    const base = pos.length / 3;
    for (let i = 0; i < X.length; i++) {
      pos.push(X[i], lake.level_m, Z[i]); shore.push(D[i]);
      if (i % SHEET_STEP === SHEET_STEP - 1) yield;
    }
    for (let q = 0; q < out.length; q++) {
      idx.push(base + out[q]);
      if (q % (3 * SHEET_STEP) === 3 * SHEET_STEP - 1) yield;
    }
    yield;                                   // the next lake
  }
  if (!idx.length) return null;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  yield;
  geo.setAttribute('aShore', new THREE.Float32BufferAttribute(shore, 1));
  yield;
  geo.setIndex(idx);
  yield;
  geo.computeBoundingSphere();
  return geo;
}

// Water chunks (TRI-CUTS): a merged world-wide water mesh has a bounding
// sphere of kilometres and is never frustum-culled. waterChunks bins the
// triangles of `geo` (`group` indices at a time: 3 = a triangle, 6 = a
// ribbon quad, kept together) by their centroid into WATER_CHUNK_M cells and
// returns one compact geometry per cell (every attribute copied, its own
// bounding sphere), in first-seen order. Same triangles, same vertices, same
// winding: only the draw is split. R8d: steps (waterChunksSteps, runSteps) —
// per CHUNK_STEP groups of the binning and of the copy, per cell, and the
// cell's assembly its own (R8d2); waterChunks = the drain.
// 4096 m (vr-smooth, 2026-11-01; was 2048): the split is a draw unit, not a
// pixel — on the Quest the frame is draw-submission bound (session 872: water
// 34 draws both eyes for 102 k tris), so a cell is as large as the frustum
// cull still earns its draw
export const WATER_CHUNK_M = 4096;
// the class's cell (quest-merge 2026-11-01): WATER_CHUNK_M × CLASS_DRAW
// water_chunk (device.js; quest 4 = 16384 m: ONE river mesh + ONE sheet mesh,
// the same triangles, no cull — a draw is the Quest frame's unit)
export const waterCellM = (cls) => WATER_CHUNK_M * classDraw('water_chunk', cls);
const CHUNK_STEP = 4096;
export function waterChunks(geo, group = 3, cell = WATER_CHUNK_M) { return drain(waterChunksSteps(geo, group, cell)); }
export function* waterChunksSteps(geo, group = 3, cell = WATER_CHUNK_M) {
  const idx = geo.index.array, P = geo.attributes.position.array;
  const cells = new Map();
  for (let t = 0; t < idx.length; t += group) {
    let x = 0, z = 0;
    for (let j = 0; j < group; j++) { x += P[idx[t + j] * 3]; z += P[idx[t + j] * 3 + 2]; }
    const k = Math.floor(x / group / cell) + ',' + Math.floor(z / group / cell);
    const c = cells.get(k);
    if (c) c.push(t); else cells.set(k, [t]);
    if ((t / group) % CHUNK_STEP === CHUNK_STEP - 1) yield;
  }
  yield;
  const names = Object.keys(geo.attributes);
  const out = [];
  for (const [key, tris] of cells) {
    const remap = new Map(), ix = [];
    const arr = names.map(() => []);
    for (let q = 0; q < tris.length; q++) {
      const t = tris[q];
      if (q % CHUNK_STEP === CHUNK_STEP - 1) yield;
      for (let j = 0; j < group; j++) {
        const v = idx[t + j];
        let w = remap.get(v);
        if (w === undefined) {
          w = remap.size;
          remap.set(v, w);
          names.forEach((n, a) => {
            const at = geo.attributes[n], sz = at.itemSize;
            for (let e = 0; e < sz; e++) arr[a].push(at.array[v * sz + e]);
          });
        }
        ix.push(w);
      }
    }
    yield;                                   // the cell's assembly (R8d2)
    const g = new THREE.BufferGeometry();
    names.forEach((n, a) => g.setAttribute(n, new THREE.Float32BufferAttribute(arr[a], geo.attributes[n].itemSize)));
    g.setIndex(ix);
    g.computeBoundingSphere();
    out.push({ key, geo: g });
    yield;
  }
  return out;
}

// one mesh per chunk, all on ONE material (one program); returns the meshes.
// The chunking in steps under the caller's long-task label (runSteps marks
// each task; act re-marks the rest of the last one).
async function waterMeshes(scene, geo, group, mat, name, label) {
  const parts = await runSteps(waterChunksSteps(geo, group, waterCellM()), label);
  act(label);
  return parts.map(({ key, geo: g }) => {
    const mesh = new THREE.Mesh(g, mat);
    mesh.name = `${name}@${key}`;
    mesh.renderOrder = 1;
    mesh.matrixAutoUpdate = false;
    scene.add(mesh);
    return mesh;
  });
}

// The sheet lakes: transparent draws chunked by WATER_CHUNK_M (depthWrite
// off, renderOrder 1, layer 0 — every camera, the optics included, sees
// them), and their footprint to the terrain (groundmat setWaterSheets: the
// bed, no surface).
export async function buildSheets(scene, meta, baseUrl) {
  const wj = await fetchJsonOnce(`${baseUrl}/water.json`).catch(() => null);
  act('water.sheets');                  // the long-task label of the rest of this task (sched.js)
  const sheets = ((wj && wj.lakes) || []).filter((l) => l.sheet && l.polygon && l.polygon.length >= 3);
  if (!sheets.length) { setWaterSheets(null); return []; }
  const areas = await runSteps(WaterAreas.build(sheets), 'water.sheets');   // R8d2: the bucketing in steps
  setWaterSheets((x, z) => areas.inside(x, z));
  // each task of the steps under 'water.sheets' (runSteps); waterMeshes goes on under it
  const geo = await runSteps(sheetGeometrySteps(sheets, Math.max(0.1, Number(WATER_CFG.sheet_ramp_m))), 'water.sheets');
  if (!geo) return [];
  const meshes = await waterMeshes(scene, geo, 3, sheetMaterial(), 'water:sheets', 'water.sheets');
  geo.dispose();
  return meshes;
}

// Check-dam falls (objects.json `checkdam.fall`, bake _checkdams) spliced
// into one ribbon: the water rides the crest at the pool level `top` (U at
// the dam's upstream face, LIP at its downstream face), drops as a sheet to
// `base` (D, leaning 0.4 m out), and a foam node P closes the plunge band.
// Upstream water is raised to `top` until the bed climbs above it — the pool
// the dam holds back — then a running min keeps the line monotone (a lower
// dam's pool drowns an upper dam's toe instead of the upper crest sinking).
// Returns nodes {x, y, z, foam}.
const DAM_HALF = 0.5;           // = objects.js checkdam thickness / 2
export function riverProfile(pts, falls) {
  const L = [];
  for (let i = 0; i + 1 < pts.length; i++) {
    L.push(Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][2] - pts[i][2]) || 1);
  }
  const nodes = pts.map((p, i) => ({ k: i, x: p[0], y: p[1], z: p[2], foam: 0 }));
  if (!falls || !falls.length) return nodes;
  const fs = falls.slice().sort((a, b) => (a.i + a.t) - (b.i + b.t));
  const add = [], cut = [];
  fs.forEach((f, n) => {
    const kc = f.i + f.t, Li = L[f.i] || 1;
    const next = fs[n + 1] ? fs[n + 1].i + fs[n + 1].t : Infinity;
    const at = (d) => ({ x: f.x + f.tx * d, z: f.z + f.tz * d, k: kc + d / Li });
    const drop = f.top - f.base > 0.05;
    const U = { ...at(-DAM_HALF), y: f.top, foam: 0, fall: f, pool: true };
    const P = drop && next - kc > 4.5 / Li ? { ...at(3.0), y: f.base, foam: 0.45 } : null;
    const seq = drop
      ? [U, { ...at(DAM_HALF), y: f.top, foam: 0.25 }, { ...at(DAM_HALF + 0.4), y: f.base, foam: 1 }]
      : [U, { ...at(DAM_HALF), y: f.top, foam: 0.1 }];
    if (P) seq.push(P);
    add.push(...seq);
    cut.push([seq[0].k - 0.3 / Li, seq[seq.length - 1].k + 0.3 / Li]);
  });
  const keep = nodes.filter((nd, i) => i === 0 || i === nodes.length - 1
    || !cut.some(([a, b]) => nd.k > a && nd.k < b));
  const all = keep.concat(add).sort((a, b) => a.k - b.k);
  // pools: everything upstream of a dam sits at least at its crest level
  for (const f of fs) {
    const u = all.find((nd) => nd.fall === f);
    for (const nd of all) if (nd.k < u.k) nd.y = Math.max(nd.y, f.top);
  }
  for (let i = 1; i < all.length; i++) all[i].y = Math.min(all[i].y, all[i - 1].y);
  return all;
}

// Stream ribbons + the river flow from the bake's rivers.json (rev >= 16).
// Vertices carry the baked thalweg elevation, lifted 0.22 m inside the
// channel; bridge decks (lifted >= 1.2 m) pass clear over them. `objectsP`
// (objects.js loadObjects) supplies the check-dam falls. A run whose both
// ends lie in a water.json body (the class-254 stamp: lakes, ponds, river
// areas) is not drawn — the terrain draws that water (WATER-G R2); the flow
// over the river areas goes to the terrain once (groundmat setWaterFlow).
// Returns { meshes, ways, areas } — ways + areas (the river areas) feed the
// RiverIndex: grass suppression, the river name on aim (one fetch each).
export async function buildRivers(scene, meta, baseUrl, objectsP) {
  if (!meta.rivers) return { meshes: [], ways: [], areas: null };
  const [rj, wj] = await Promise.all([
    fetchAsset(`${baseUrl}/${meta.rivers.file}`, { prio: 'bulk', as: 'json' }),
    fetchJsonOnce(`${baseUrl}/water.json`).catch(() => null),
  ]);
  if (!rj || !rj.ways) return { meshes: [], ways: [], areas: null };
  act('water.rivers');                  // the long-task label of the rest of this task (sched.js)
  // R8d2: the areas' bucketing and the flow's segment grid in steps (one
  // ~250 ms task on the live bake: water.js WaterAreas.build, flowFieldSteps)
  const areas = await runSteps(WaterAreas.build((wj && wj.river_areas) || []), 'water.rivers');
  const wet = await runSteps(WaterAreas.build([...((wj && wj.lakes) || []), ...((wj && wj.river_areas) || [])]), 'water.rivers');
  setWaterFlow(await runSteps(flowFieldSteps(rj.ways, areas), 'water.rivers'));
  const od = objectsP ? await objectsP.catch(() => null) : null;
  act('water.rivers');
  const geo = await runSteps(riverRibbonSteps(rj.ways, (od && od.objects) || [], wet), 'water.rivers');
  if (!geo) return { meshes: [], ways: rj.ways, areas };
  // each node's vertex pair about its axis: the thin-ribbon widening (streamMaterial; the chunks copy it)
  geo.setAttribute('aOff', new THREE.Float32BufferAttribute(ribbonOffsets(geo.attributes.position.array), 3));
  const meshes = await waterMeshes(scene, geo, 6, streamMaterial(), 'rivers', 'water.rivers');
  geo.dispose();
  return { meshes, ways: rj.ways, areas };
}

// The stream ribbons of buildRivers as a STEP GENERATOR (R8d; runSteps): a
// step per way and per RIB_STEP nodes of a long one, the assembly a step per
// attribute (R8d2); -> the ribbon geometry
// (position, aFlow, aDir, the index) or null when no run is drawn. `objects`
// = objects.json's (the check-dam falls), `wet` = the class-254 bodies.
const RIB_STEP = 512;
export function* riverRibbonSteps(ways, objects, wet) {
  const byWay = new Map();
  for (const o of objects) {
    if (o.a !== 'checkdam' || !o.fall || !ways[o.fall.w]) continue;
    if (!byWay.has(o.fall.w)) byWay.set(o.fall.w, []);
    byWay.get(o.fall.w).push(o.fall);
  }
  const pos = [];
  const flow = [];
  const dir = [];
  const idx = [];
  let vi = 0;
  for (let wi = 0; wi < ways.length; wi++) {
    const way = ways[wi];
    if (way.pts.length < 2) continue;
    const nd = riverProfile(way.pts, byWay.get(wi));
    const half = way.w / 2;
    const inW = nd.map((p) => wet.inside(p.x, p.z));
    let s = 0;
    for (let i = 0; i < nd.length; i++) {
      const p = nd[i];
      const a = nd[Math.max(0, i - 1)];
      const b = nd[Math.min(nd.length - 1, i + 1)];
      let dx = b.x - a.x; let dz = b.z - a.z;
      const len = Math.hypot(dx, dz) || 1;
      dx /= len; dz /= len;
      if (i > 0) s += Math.hypot(p.x - a.x, p.y - a.y, p.z - a.z);
      // speed ~ sqrt(slope) over the neighbours: a pool (slope ~0) crawls at
      // 0.12 m/s, a 10 % reach runs ~1.5 m/s, a sheet (vertical) caps at 4
      const slope = Math.max(0, a.y - b.y) / len;
      const v = riverSpeed(slope);
      const foam = Math.max(p.foam, Math.min(0.55, Math.max(0, slope - 0.2) * 1.6));
      const y = p.y + 0.22;
      pos.push(p.x - dz * half, y, p.z + dx * half,
               p.x + dz * half, y, p.z - dx * half);
      flow.push(s, v, foam, -1, s, v, foam, 1);
      dir.push(dx, dz, dx, dz);
      // a run over painted water (both ends in a class-254 body) is the terrain's
      if (i > 0 && !(inW[i - 1] && inW[i])) {
        const vv = vi + i * 2;
        idx.push(vv - 2, vv - 1, vv, vv - 1, vv + 1, vv);
      }
      if (i % RIB_STEP === RIB_STEP - 1) yield;
    }
    vi += nd.length * 2;
    yield;                                   // the next way
  }
  if (!idx.length) return null;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  yield;
  geo.setAttribute('aFlow', new THREE.Float32BufferAttribute(flow, 4));
  yield;
  geo.setAttribute('aDir', new THREE.Float32BufferAttribute(dir, 2));
  yield;
  geo.setIndex(idx);
  return geo;
}
